import type { MoldePageCandidate } from '../types';

export type MoldeV3Mode = 'estrutural' | 'texto';
export type MoldeV3UiPiece = 'corpo' | 'manga' | 'punho' | 'barra' | 'gola' | 'short';
export type MoldeSizeFamily = 'infantil' | 'babylook' | 'tradicional' | 'especial' | 'custom';

export interface MoldeV3Option {
  value: string;
  label: string;
}

export const MOLDE_SIZE_FAMILY_OPTIONS: Array<{ value: MoldeSizeFamily; label: string }> = [
  { value: 'infantil', label: 'Infantil' },
  { value: 'babylook', label: 'Babylook' },
  { value: 'tradicional', label: 'Tradicional' },
  { value: 'especial', label: 'Especial' },
  { value: 'custom', label: 'Custom' },
];

export const MOLDE_SIZE_OPTIONS_BY_FAMILY: Record<MoldeSizeFamily, string[]> = {
  infantil: ['2', '4', '6', '8', '10', '12'],
  babylook: ['BBL P', 'BBL M', 'BBL G', 'BBL GG'],
  tradicional: ['PP', 'P', 'M', 'G', 'GG'],
  especial: ['G1', 'G2', 'G3'],
  custom: [],
};

export interface MoldeV3UiState {
  mode: MoldeV3Mode;
  piece: MoldeV3UiPiece;
  part: string;
  side: string;
  placeholder: string;
}

export const MOLDE_V3_PLACEHOLDERS: MoldeV3Option[] = [
  { value: 'nome', label: 'Nome' },
  { value: 'sobrenome', label: 'Sobrenome' },
  { value: 'nome_completo', label: 'Nome completo' },
  { value: 'numero', label: 'Numero' },
  { value: 'observacao', label: 'Observacao' },
  { value: 'tamanho', label: 'Tamanho / Etiqueta' },
];

export const MOLDE_V3_PIECES: Array<{ value: MoldeV3UiPiece; label: string }> = [
  { value: 'corpo', label: 'Corpo' },
  { value: 'manga', label: 'Manga' },
  { value: 'punho', label: 'Punho' },
  { value: 'barra', label: 'Barra' },
  { value: 'gola', label: 'Gola' },
  { value: 'short', label: 'Short' },
];

const PART_OPTIONS: Record<MoldeV3UiPiece, MoldeV3Option[]> = {
  corpo: [
    { value: 'frente', label: 'Frente' },
    { value: 'costas', label: 'Costas' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'recorte_frente', label: 'Recorte Frente' },
    { value: 'recorte_costas', label: 'Recorte Costas' },
    { value: 'faixa', label: 'Faixa' },
    { value: 'peitilho', label: 'Peitilho' },
  ],
  manga: [
    { value: 'curta', label: 'Curta' },
    { value: 'longa', label: 'Longa' },
    { value: 'raglan', label: 'Raglan' },
  ],
  punho: [{ value: 'punho', label: 'Punho' }],
  barra: [
    { value: 'barra_corpo', label: 'Barra do corpo' },
    { value: 'barra_manga', label: 'Barra da manga' },
    { value: 'barra_short', label: 'Barra do short' },
  ],
  gola: [
    { value: 'O', label: 'O' },
    { value: 'V', label: 'V' },
    { value: 'polo', label: 'Polo' },
    { value: 'U', label: 'U' },
    { value: 'vies', label: 'Vies' },
  ],
  short: [
    { value: 'frente', label: 'Frente' },
    { value: 'costas', label: 'Costas' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'cos', label: 'Cos' },
    { value: 'entrepernas', label: 'Entrepernas' },
  ],
};

const UNIQUE_SIDE: MoldeV3Option[] = [{ value: '', label: 'Unico' }];
const SIDE_OPTIONS: MoldeV3Option[] = [
  { value: '', label: 'Unico' },
  { value: 'dir', label: 'Direito' },
  { value: 'esq', label: 'Esquerdo' },
];

export function getPartOptions(piece: MoldeV3UiPiece) {
  return PART_OPTIONS[piece];
}

export function getSideOptions(piece: MoldeV3UiPiece, part: string) {
  if (piece === 'manga') return SIDE_OPTIONS;
  if (piece === 'punho') return SIDE_OPTIONS;
  if (piece === 'barra' && part === 'barra_manga') return SIDE_OPTIONS;
  return UNIQUE_SIDE;
}

export function itemSupportsTextMode(item: MoldePageCandidate | null) {
  if (!item) return false;
  return Boolean(item.isText || item.peca === 'texto' || item.state === 'placeholder');
}

export function getDefaultUiState(): MoldeV3UiState {
  return {
    mode: 'estrutural',
    piece: 'corpo',
    part: 'frente',
    side: '',
    placeholder: 'nome_completo',
  };
}

export function getUiStateFromCandidate(item: MoldePageCandidate | null): MoldeV3UiState {
  if (!item) return getDefaultUiState();

  if (item.peca === 'texto' || item.isText || item.state === 'placeholder') {
    return {
      mode: 'texto',
      piece: 'corpo',
      part: 'frente',
      side: '',
      placeholder: item.placeholder || 'nome_completo',
    };
  }

  if (item.peca === 'manga' && item.parte === 'punho') {
    return {
      mode: 'estrutural',
      piece: 'punho',
      part: 'punho',
      side: item.lado || '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'extra' && item.parte === 'barra_corpo') {
    return {
      mode: 'estrutural',
      piece: 'barra',
      part: 'barra_corpo',
      side: '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'extra' && item.parte === 'barra_manga') {
    return {
      mode: 'estrutural',
      piece: 'barra',
      part: 'barra_manga',
      side: item.lado || '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'inferior' && item.parte === 'barra') {
    return {
      mode: 'estrutural',
      piece: 'barra',
      part: 'barra_short',
      side: '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'inferior') {
    return {
      mode: 'estrutural',
      piece: 'short',
      part: item.parte || 'frente',
      side: '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'gola') {
    return {
      mode: 'estrutural',
      piece: 'gola',
      part: item.parte || 'O',
      side: '',
      placeholder: 'nome_completo',
    };
  }

  if (item.peca === 'manga') {
    const legacyMangaPartMap: Record<string, string> = {
      inteira: 'longa',
      frente: 'curta',
      costas: 'curta',
      raglan_frente: 'raglan',
      raglan_costas: 'raglan',
    };

    return {
      mode: 'estrutural',
      piece: 'manga',
      part: legacyMangaPartMap[item.parte || ''] || item.parte || 'curta',
      side: item.lado || '',
      placeholder: 'nome_completo',
    };
  }

  return {
    mode: 'estrutural',
    piece: 'corpo',
    part: item.parte || 'frente',
    side: '',
    placeholder: 'nome_completo',
  };
}

export function normalizeUiState(state: MoldeV3UiState): MoldeV3UiState {
  if (state.mode === 'texto') {
    return {
      ...state,
      side: '',
      placeholder: MOLDE_V3_PLACEHOLDERS.some((item) => item.value === state.placeholder)
        ? state.placeholder
        : 'nome_completo',
    };
  }

  const piece = MOLDE_V3_PIECES.some((item) => item.value === state.piece) ? state.piece : 'corpo';
  const partOptions = getPartOptions(piece);
  const part = partOptions.some((item) => item.value === state.part) ? state.part : partOptions[0]?.value || '';
  const sideOptions = getSideOptions(piece, part);
  const side = sideOptions.some((item) => item.value === state.side) ? state.side : sideOptions[0]?.value || '';

  return {
    ...state,
    piece,
    part,
    side,
    placeholder: 'nome_completo',
  };
}

export function buildMoldePayload(state: MoldeV3UiState) {
  if (state.mode === 'texto') {
    return {
      peca: 'texto',
      parte: '',
      lado: '',
      escala: 'fixo',
      placeholder: state.placeholder,
    };
  }

  if (state.piece === 'punho') {
    return {
      peca: 'manga',
      parte: 'punho',
      lado: state.side,
      escala: 'proporcional',
      placeholder: '',
    };
  }

  if (state.piece === 'barra') {
    if (state.part === 'barra_corpo') {
      return {
        peca: 'extra',
        parte: 'barra_corpo',
        lado: '',
        escala: 'proporcional',
        placeholder: '',
      };
    }

    if (state.part === 'barra_manga') {
      return {
        peca: 'extra',
        parte: 'barra_manga',
        lado: state.side,
        escala: 'proporcional',
        placeholder: '',
      };
    }

    return {
      peca: 'inferior',
      parte: 'barra',
      lado: '',
      escala: 'proporcional',
      placeholder: '',
    };
  }

  if (state.piece === 'short') {
    return {
      peca: 'inferior',
      parte: state.part,
      lado: '',
      escala: 'proporcional',
      placeholder: '',
    };
  }

  if (state.piece === 'gola') {
    return {
      peca: 'gola',
      parte: state.part,
      lado: '',
      escala: 'proporcional',
      placeholder: '',
    };
  }

  if (state.piece === 'manga') {
    return {
      peca: 'manga',
      parte: state.part,
      lado: state.side,
      escala: 'proporcional',
      placeholder: '',
    };
  }

  return {
    peca: 'corpo',
    parte: state.part,
    lado: '',
    escala: 'proporcional',
    placeholder: '',
  };
}

export function buildPreviewLabel(state: MoldeV3UiState) {
  const payload = buildMoldePayload(state);
  if (payload.peca === 'texto') return `texto.${payload.placeholder}`;
  return [payload.peca, payload.parte, payload.lado].filter(Boolean).join('.');
}

export function getCandidateLabel(item: MoldePageCandidate) {
  if (item.identificador) return item.identificador;
  return item.isText ? 'Texto sem mapeamento' : 'Objeto sem mapeamento';
}
